import { WaitlistPage } from './pages/AdminOps/WaitlistPage';
import { Component, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, Redirect, Route, Router, Switch, useLocation, useParams, useSearch } from 'wouter';

import { SessionProvider, useSession } from './session';
import { ParentEvidencePage } from './pages/Learning/ParentEvidencePage';
import { LoginPage } from './pages/Login';
import { BillingPage } from './pages/Billing';
import { ManagedAiPage } from './pages/ManagedAi/ManagedAiPage';
import { BillingApiError, abandonCheckoutIntent, rememberPendingCheckoutSessionRef, startCheckout } from './api/billing';
import { fetchManagedFunding, isManagedAiFeatureEnabled } from './api/managedAi';
import { BillingModeBanner } from './components/BillingModeBanner/BillingModeBanner';
import { BillingStatusBanner } from './components/BillingStatusBanner/BillingStatusBanner';
import { PricingPage } from './pages/Pricing';
import { ContextMemoryPage } from './pages/ContextMemory';
import { ProjectBrainPage } from './pages/ProjectBrain';
import { TasksPage } from './pages/Tasks';
import { EvalsPage } from './pages/Evals';
import { ModelsPage } from './pages/Models';
import { SavingsPage } from './pages/Savings';
import CampaignsPage, { CampaignDetailPage } from './pages/Campaigns';
import RunsPage from './pages/Runs';
import ApprovalsPage from './pages/Approvals';
import DevicesPage from './pages/Devices';
import { PairLocalNodePage } from './pages/Devices/DevicesPage';
import ReceiptsPage from './pages/Receipts';
import ProviderKeysPage from './pages/ProviderKeys/ProviderKeysPage';
import { NotificationOutboxPanel, TenantDetailPage, TenantListPage } from './pages/AdminOps';
import { listTenants } from './pages/AdminOps/adminOpsApi';
import { EmailVerificationPage, PasswordResetPage } from './pages/Account/AccountFlowPages';
import { ProjectOverviewPage, ProjectsPage } from './pages/Projects/ProjectsPage';
import { OnboardingPage } from './pages/Onboarding';
import { ConnectPage } from './pages/Connect/ConnectPage';
import { WakeWorkspacePage } from './pages/Projects/WakeWorkspacePage';
import { safeReturnTo } from './pages/Onboarding/OnboardingPage';
import { SignupPage } from './pages/Signup';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { SupportPage } from './pages/Support/SupportPage';
import { StudioLink } from './components/StudioLink/StudioLink';
import { AccountMenu } from './components/AccountMenu/AccountMenu';
import { TermsGate, TermsGateProvider } from './components/TermsGate';
import { settingsSectionFromSearch } from './components/Settings/settingsSections';
import { getStoragePreferences } from './api/storage';
import {
  clearPendingOffer,
  getPendingOffer,
  resolvePendingOffer,
  setPendingOffer,
} from './lib/pendingOffer';
import { offerPriceLabel } from './lib/offerPrice';
import { deepLinkOffer } from './lib/unlockDeepLink';
import { forgetSignedInAccount } from './lib/signedInAccount';

const WELCOME_PROMPTED_KEY = 'auteur.welcome.prompted';

function welcomePromptWasShown(): boolean {
  try {
    return window.sessionStorage.getItem(WELCOME_PROMPTED_KEY) === '1';
  } catch {
    return false;
  }
}

function markWelcomePromptShown(): void {
  try {
    window.sessionStorage.setItem(WELCOME_PROMPTED_KEY, '1');
  } catch {
    // The in-memory guard still prevents a redirect loop for this app session.
  }
}

function clearWelcomePromptShown(): void {
  try {
    window.sessionStorage.removeItem(WELCOME_PROMPTED_KEY);
  } catch {
    // Session storage can be unavailable in restricted browser contexts.
  }
}

export const CUSTOMER_ROUTES = Object.freeze([
  '/login', '/signup', '/password-reset', '/verify-email', '/pricing', '/support', '/projects',
  '/welcome',
  '/projects/:projectId', '/projects/:projectId/brain', '/projects/:projectId/memory',
  '/projects/:projectId/tasks',
  '/billing/checkout', '/billing', '/managed-ai', '/receipts', '/devices', '/devices/pair', '/settings',
  '/settings/provider-keys', '/connect', '/campaigns', '/campaigns/:campaignId', '/runs',
  '/approvals', '/evals', '/models', '/savings', '/admin/tenants', '/admin/tenants/:orgId', '/admin/waitlist',
]);

function NavItem({ href, area, children }: { href: string; area?: string; children: ReactNode }) {
  const [location] = useLocation();
  return (
    <Link to={href} data-area={area} className={location === href || location.startsWith(`${href}/`) ? 'active' : ''}>
      {children}
    </Link>
  );
}

/** The site's gold spiral mark (auteurintelligence.com, theme/icon.svg), drawn inline. */
function BrandLogo() {
  return (
    <svg className="brand-logo" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="auteur-brand-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8a6a33" />
          <stop offset=".65" stopColor="#C99A4B" />
          <stop offset="1" stopColor="#ffdf9e" />
        </linearGradient>
      </defs>
      <path d="M21 19 L21 5 L7 5 L7 16 L17 16 L17 8 L10 8 L10 13 L14 13 L14 10.5 L12 10.5" fill="none"
        stroke="url(#auteur-brand-gold)" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" />
      <rect x="19.9" y="17.9" width="2.2" height="2.2" fill="#C99A4B" />
      <rect x="10.9" y="9.4" width="2.2" height="2.2" fill="#fff1cf" />
    </svg>
  );
}

/** One wordmark everywhere (walkthrough F-36): "Auteur Intelligence™". */
export const BRAND_NAME = 'Auteur Intelligence™';

export function AppFrame({ children }: { children: ReactNode }) {
  const { session, error: sessionError } = useSession();
  const search = useSearch();
  const [location, navigate] = useLocation();
  const deepLinkSection = settingsSectionFromSearch(search);
  const welcomePromptHandled = useRef(false);
  const hadSession = useRef(false);
  const latestSession = useRef(session);
  latestSession.current = session;
  const [managedAiVisible, setManagedAiVisible] = useState(false);

  useEffect(() => {
    if (!session) {
      setManagedAiVisible(false);
      return;
    }
    let active = true;
    fetchManagedFunding()
      .then((status) => {
        if (active && isManagedAiFeatureEnabled(status)) {
          setManagedAiVisible(true);
        }
      })
      .catch(() => {
        if (active) setManagedAiVisible(false);
      });
    return () => {
      active = false;
    };
  }, [session]);

  useEffect(() => {
    if (session) {
      hadSession.current = true;
    } else if (session === null) {
      // Signed out (not merely unreachable): the address the top bar showed belongs to nobody now (F-38).
      if (!sessionError) forgetSignedInAccount();
      if (hadSession.current) {
        clearWelcomePromptShown();
        welcomePromptHandled.current = false;
        hadSession.current = false;
      }
    }
  }, [session, sessionError]);

  // Settings has one home, the /settings page (walkthrough F-9). A `?settings=<section>` link
  // made for the former pop-up opens that section there instead.
  useEffect(() => {
    if (session && deepLinkSection && location !== '/settings') {
      navigate('/settings?' + search, { replace: true });
    }
  }, [session, deepLinkSection, location, search, navigate]);

  useEffect(() => {
    if (!session) return;
    if (location === '/welcome') {
      welcomePromptHandled.current = true;
      markWelcomePromptShown();
      return;
    }
    if (welcomePromptHandled.current || welcomePromptWasShown()) {
      welcomePromptHandled.current = true;
      return;
    }

    welcomePromptHandled.current = true;
    markWelcomePromptShown();
    const orgId = session.org_id;
    const returnTo = location + (search ? '?' + search : '');
    getStoragePreferences()
      .then((preferences) => {
        if (latestSession.current?.org_id === orgId && preferences.defaults_confirmed_at === null) {
          navigate('/welcome?return_to=' + encodeURIComponent(returnTo), { replace: true });
        }
      })
      .catch(() => {
        // Let the customer continue using the app if the preferences check is unavailable.
      });
  }, [session, location, search, navigate]);

  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="app-header">
        <Link className="brand" to={session ? '/projects' : '/login'} aria-label={`${BRAND_NAME} home`}>
          <BrandLogo />
          <span className="brand-word" aria-hidden="true">
            <span className="brand-a">A</span>uteur<span className="brand-sub">Intelligence</span><sup className="brand-tm">™</sup>
          </span>
        </Link>
        <nav aria-label="Primary navigation">
          {session ? (
            <>
              <StudioLink />
              {/* F-72: projects are created in the studio, and hosted runs exist only for the owner org. A
                  customer's "Projects" is their home: open the studio from there (F-66, the way back). */}
              <NavItem href="/projects" area="projects">{session.owner_org === true ? 'Projects' : 'Your studio'}</NavItem>
              {session.owner_org === true ? <NavItem href="/runs" area="runs">Runs</NavItem> : null}
              <NavItem href="/receipts" area="billing">Receipts</NavItem>
              <NavItem href="/billing" area="billing">Billing</NavItem>
              {managedAiVisible ? <NavItem href="/managed-ai" area="billing">Managed AI</NavItem> : null}
              <NavItem href="/devices" area="devices">Devices</NavItem>
              <NavItem href="/connect" area="connect">Connect AI</NavItem>
              <NavItem href="/welcome" area="setup">Setup</NavItem>
              <AccountMenu />
            </>
          ) : (
            <>
              <NavItem href="/pricing">Pricing</NavItem>
              <NavItem href="/signup">Create account</NavItem>
              <NavItem href="/login">Sign in</NavItem>
            </>
          )}
        </nav>
      </header>
      <BillingModeBanner />
      <BillingStatusBanner />
      <main id="main-content" className="app-content">{children}</main>
      <footer className="app-footer"><span>{BRAND_NAME}</span><Link to="/support">Support</Link></footer>
    </div>
  );
}

export function AuthenticatedRoute({ children }: { children: ReactNode }) {
  const { session, error } = useSession();
  const [location] = useLocation();
  const search = useSearch();
  if (session === undefined) return <p className="route-state" role="status">Checking session…</p>;
  if (error) return <div className="route-state error" role="alert"><h1>Session unavailable</h1><p>{error}</p></div>;
  if (!session) return <Redirect to={`/login?next=${encodeURIComponent(authenticatedNext(location, search))}`} replace />;
  // Owner decision 2026-10-08: no workspace use until the current terms are accepted.
  return <TermsGate>{children}</TermsGate>;
}

export function authenticatedNext(location: string, search: string): string {
  return location + (search ? '?' + search : '');
}

export function postLoginDestination(search: string): string {
  const params = new URLSearchParams(search);
  if (params.has('return_to')) {
    const returnTo = safeReturnTo(search);
    if (returnTo === '/welcome') return '/welcome';
    return '/welcome?return_to=' + encodeURIComponent(returnTo);
  }
  if (getPendingOffer()) {
    return '/welcome?return_to=' + encodeURIComponent('/billing/checkout');
  }
  const rawNext = params.get('next') || '/projects';
  return rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/projects';
}

export function AdminAuthorizationState({ state, message = '' }: { state: 'loading' | 'allowed' | 'denied' | 'error'; message?: string }) {
  if (state === 'loading') return <p className="route-state" role="status">Checking administrator authorization…</p>;
  if (state === 'denied') return <div className="route-state denied" role="alert"><h1>Access denied</h1><p>This route requires administrator authorization confirmed by the API.</p></div>;
  if (state === 'error') return <div className="route-state error" role="alert"><h1>Authorization check failed</h1><p>{message}</p></div>;
  return null;
}

/** How a failed operator probe renders: 401/403 is a denial, anything else an error. */
export function adminAuthorizationOutcome(failure: unknown): { state: 'denied' | 'error'; message: string } {
  const status = (failure as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return { state: 'denied', message: '' };
  return {
    state: 'error',
    message: failure instanceof Error ? failure.message : 'administrator authorization could not be checked',
  };
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied' | 'error'>('loading');
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    listTenants()
      .then(() => active && setState('allowed'))
      .catch((failure: unknown) => {
        if (!active) return;
        const outcome = adminAuthorizationOutcome(failure);
        setMessage(outcome.message);
        setState(outcome.state);
      });
    return () => { active = false; };
  }, []);
  if (state !== 'allowed') return <AdminAuthorizationState state={state} message={message} />;
  return <>{children}</>;
}

/**
 * The native hosted Brain, Evals, Learning, owner bridge and hosted tasks APIs serve only
 * the deployment's owner org and answer every other org 404 (DESIGN.md §18),
 * so their pages render the ordinary 404 unless the server's session says
 * this org is the owner org.
 */
export function OwnerOrgRoute({ children }: { children: ReactNode }) {
  const { session } = useSession();
  if (session?.owner_org !== true) return <NotFoundPage />;
  return <>{children}</>;
}

function LoginRoute() {
  const { session, setSession } = useSession();
  const [, navigate] = useLocation();
  const search = useSearch();
  const destination = postLoginDestination(search);
  if (session) return <Redirect to={destination} replace />;
  return <LoginPage onSignedIn={(value) => { setSession(value); navigate(destination, { replace: true }); }} />;
}

function BillingOfferCheckoutRoute() {
  // A workspace's "How to unlock" hint links here with ?offer=<catalog id> for a signed-in customer
  // adding a product. It only selects the offer: the server still confirms it (offer preview) and
  // nothing is charged until the customer starts checkout below.
  const search = useSearch();
  const [checked, setChecked] = useState(false);
  const [selection, setSelection] = useState<Awaited<ReturnType<typeof resolvePendingOffer>>>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [stuckCheckout, setStuckCheckout] = useState(false);

  useEffect(() => {
    let active = true;
    const linkedOffer = deepLinkOffer(search);
    if (linkedOffer) setPendingOffer({ id: linkedOffer, interval: 'month' });
    resolvePendingOffer()
      .then((value) => {
        if (!active) return;
        setSelection(value);
        setChecked(true);
      })
      .catch(() => {
        if (!active) return;
        clearPendingOffer();
        setUnavailable(true);
        setChecked(true);
      });
    return () => { active = false; };
  }, []);

  async function beginCheckout() {
    if (!selection || busy) return;
    setBusy(true);
    setCheckoutError('');
    setStuckCheckout(false);
    try {
      const handoff = await startCheckout({ offerId: selection.pending.id });
      rememberPendingCheckoutSessionRef(handoff.session_ref);
      clearPendingOffer();
      window.location.assign(handoff.redirect_url);
    } catch (failure) {
      setBusy(false);
      if (failure instanceof BillingApiError && failure.status === 409) setStuckCheckout(true);
      setCheckoutError(
        failure instanceof BillingApiError
          ? failure.message
          : 'checkout could not be started',
      );
    }
  }

  if (!checked) {
    return <p className="billing-checkout-loading" role="status">Checking the selected offer…</p>;
  }
  if (unavailable) {
    return (
      <section className="billing-checkout-page">
        <h1>Selected offer unavailable</h1>
        <p role="status">The selected offer could not be confirmed and was cleared. Choose a plan again to continue.</p>
        <Link to="/pricing">Browse plans</Link>
      </section>
    );
  }
  if (!selection) {
    return (
      <section className="billing-checkout-page">
        <h1>No selected offer</h1>
        <p>No offer is pending checkout in this browser session.</p>
        <Link to="/pricing">Browse plans</Link>
      </section>
    );
  }

  return (
    <section className="billing-checkout-page">
      <p className="eyebrow">Checkout</p>
      <h1>Confirm your plan</h1>
      <p>{selection.preview.name}</p>
      <p>Price: {offerPriceLabel(selection.preview)}</p>
      <p>Billed monthly</p>
      <p>The final price and payment session are resolved by the billing service.</p>
      {checkoutError ? <p role="alert">{checkoutError}</p> : null}
      {stuckCheckout ? <div><p>A previous checkout may still exist. Check your billing before starting another purchase.</p>
        <button type="button" onClick={() => { abandonCheckoutIntent({ offerId: selection.pending.id }); setStuckCheckout(false); setCheckoutError(''); }}>Forget previous checkout and start over</button></div> : null}
      <button type="button" disabled={busy} onClick={beginCheckout}>
        {busy ? 'Starting checkout…' : 'Continue to secure checkout'}
      </button>
      <p><Link to="/billing">Back to billing</Link></p>
    </section>
  );
}

function ProjectRoute({ page }: { page: 'overview' | 'brain' | 'memory' | 'tasks' }) {
  const { projectId = '' } = useParams<{ projectId: string }>();
  if (page === 'brain') return <ProjectBrainPage projectId={projectId} />;
  if (page === 'memory') return <ContextMemoryPage projectId={projectId} />;
  if (page === 'tasks') return <TasksPage projectId={projectId} />;
  return <ProjectOverviewPage projectId={projectId} />;
}

function CampaignDetailRoute() {
  return <CampaignDetailPage campaignId={useParams<{ campaignId: string }>().campaignId || ''} />;
}

function AdminTenantRoute() {
  return <TenantDetailPage orgId={useParams<{ orgId: string }>().orgId || ''} />;
}

function AdminListRoute() {
  const [, navigate] = useLocation();
  return (
    <>
      <NotificationOutboxPanel />
      <p><Link to="/admin/waitlist">Manage waitlist</Link></p>
      <TenantListPage
        onSelectTenant={(orgId: string) =>
          navigate(`/admin/tenants/${encodeURIComponent(orgId)}`)
        }
      />
    </>
  );
}

export function NotFoundPage() {
  return <section className="route-state"><p className="eyebrow">404</p><h1>Page not found</h1><p>The requested customer route does not exist.</p><Link to="/">Return home</Link></section>;
}

interface BoundaryState { error: Error | null; }
export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): BoundaryState { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return <main className="route-state error" role="alert"><p className="eyebrow">Application error</p><h1>Something went wrong</h1><p>{this.state.error.message}</p><a href="/">Return home</a></main>;
  }
}

function Protected({ children }: { children: ReactNode }) {
  return <AuthenticatedRoute>{children}</AuthenticatedRoute>;
}

export function AppRoutes() {
  return (
    <Switch>
      <Route path="/"><Redirect to="/projects" replace /></Route>
      <Route path="/login"><LoginRoute /></Route>
      <Route path="/signup"><SignupPage /></Route>
      <Route path="/password-reset"><PasswordResetPage /></Route>
      <Route path="/verify-email"><EmailVerificationPage /></Route>
      <Route path="/pricing"><PricingPage /></Route>
      <Route path="/support"><SupportPage /></Route>
      <Route path="/welcome"><Protected><OnboardingPage /></Protected></Route>
      <Route path="/projects"><Protected><ProjectsPage /></Protected></Route>
      <Route path="/wake"><Protected><WakeWorkspacePage /></Protected></Route>
      <Route path="/connect"><Protected><ConnectPage /></Protected></Route>
      <Route path="/projects/:projectId/brain"><Protected><OwnerOrgRoute><ProjectRoute page="brain" /></OwnerOrgRoute></Protected></Route>
      <Route path="/projects/:projectId/memory"><Protected><OwnerOrgRoute><ProjectRoute page="memory" /></OwnerOrgRoute></Protected></Route>
      <Route path="/projects/:projectId/tasks"><Protected><OwnerOrgRoute><ProjectRoute page="tasks" /></OwnerOrgRoute></Protected></Route>
      <Route path="/projects/:projectId"><Protected><OwnerOrgRoute><ProjectRoute page="overview" /></OwnerOrgRoute></Protected></Route>
      <Route path="/billing/checkout"><Protected><BillingOfferCheckoutRoute /></Protected></Route>
      <Route path="/billing"><Protected><BillingPage /></Protected></Route>
      <Route path="/managed-ai"><Protected><ManagedAiPage /></Protected></Route>
      <Route path="/receipts"><Protected><ReceiptsPage /></Protected></Route>
      <Route path="/devices/pair"><Protected><PairLocalNodePage /></Protected></Route>
      <Route path="/devices"><Protected><DevicesPage /></Protected></Route>
      <Route path="/learning"><Protected><ParentEvidencePage /></Protected></Route>
      <Route path="/settings/provider-keys"><Protected><ProviderKeysPage /></Protected></Route>
      <Route path="/settings"><Protected><SettingsPage /></Protected></Route>
      {/* Campaigns federate the deployment owner's workspaces and Owner History:
          operator-only on the server, so operator-only here too. */}
      <Route path="/campaigns/:campaignId"><Protected><AdminRoute><CampaignDetailRoute /></AdminRoute></Protected></Route>
      <Route path="/campaigns"><Protected><AdminRoute><CampaignsPage /></AdminRoute></Protected></Route>
      <Route path="/runs"><Protected><OwnerOrgRoute><RunsPage campaignId={undefined} /></OwnerOrgRoute></Protected></Route>
      <Route path="/approvals"><Protected><ApprovalsPage /></Protected></Route>
      <Route path="/evals"><Protected><OwnerOrgRoute><EvalsPage /></OwnerOrgRoute></Protected></Route>
      <Route path="/models"><Protected><OwnerOrgRoute><ModelsPage /></OwnerOrgRoute></Protected></Route>
      <Route path="/savings"><Protected><OwnerOrgRoute><SavingsPage /></OwnerOrgRoute></Protected></Route>
      <Route path="/admin/waitlist"><Protected><AdminRoute><WaitlistPage /></AdminRoute></Protected></Route>
      <Route path="/admin/tenants/:orgId"><Protected><AdminRoute><AdminTenantRoute /></AdminRoute></Protected></Route>
      <Route path="/admin/tenants"><Protected><AdminRoute><AdminListRoute /></AdminRoute></Protected></Route>
      <Route path="/admin"><Redirect to="/admin/tenants" replace /></Route>
      <Route><NotFoundPage /></Route>
    </Switch>
  );
}

export function App({ ssrPath }: { ssrPath?: string } = {}) {
  return (
    <Router ssrPath={ssrPath}>
      <SessionProvider>
        <TermsGateProvider>
          <AppErrorBoundary><AppFrame><AppRoutes /></AppFrame></AppErrorBoundary>
        </TermsGateProvider>
      </SessionProvider>
    </Router>
  );
}
